import * as tf from "@tensorflow/tfjs";

/**
 * Computes an index array according to the multi-attribute sorting of ImmaSort.
 * @param points the datapoints to be sorted
 * @param monotonicity the monotonicity direction for each dimension, specifying either -1 for decreasing or 1 for increasing trends
 * @param weights the preference weight for each dimension, larger weights cause the increase the monotonicity constraint of that dimension.
 *  The weights are supposed to sum up to 1.
 * @param terminationThreshold the delta error threshold at which the iterative optimization algorithm terminates.
 * @returns
 */
export function immaSort(
    points: number[][],
    monotonicity: number[],
    weights: number[],
    terminationThreshold: number
): number[] {
    // check input
    if (points.length < 1) {
        console.warn("No Points given");
        return Array.from(Array(points.length).keys());
    }
    const numAttrib = points[0].length;
    for (let i = 1; i < points.length; ++i) {
        if (points[i].length !== numAttrib) {
            console.warn("Inconsistent number of dimensions");
            return Array.from(Array(points.length).keys());
        }
    }
    if (monotonicity.length !== numAttrib || weights.length !== numAttrib) {
        console.warn("Invalid configuration input");
        return Array.from(Array(points.length).keys());
    }

    const weightsSum = weights.reduce((prev, curr) => prev + curr);
    if (weightsSum === 0) {
        console.warn("Can not sort with weights that sum up to 0");
        return Array.from(Array(points.length).keys());
    }
    if (weightsSum !== 1) {
        console.warn(
            "Dimension Preference Weights are supposed to sum up to 1, normalizing"
        );
        weights = weights.map(x => x / weightsSum);
    }

    // normalize input data
    const min = points.reduce(
        (prev, curr) => prev.map((d, i) => Math.min(d, curr[i])),
        Array(numAttrib).fill(Infinity)
    );
    const max = points.reduce(
        (prev, curr) => prev.map((d, i) => Math.max(d, curr[i])),
        Array(numAttrib).fill(-Infinity)
    );
    const normalizedPoints: number[][] = points.map(p =>
        p.map((d, i) => {
            const range = max[i] - min[i];
            return range > 0 ? (d - min[i]) / range : 0;
        })
    );

    // initialize preset control points
    const p0 = monotonicity.map(d => 0.5 * (1 - d));
    const p3 = monotonicity.map(d => 0.5 * (1 + d));

    // initialize random control points
    let curve: { p1: number[]; p2: number[] } = {
        p1: Array<number>(numAttrib)
            .fill(0)
            .map(() => Math.random()),
        p2: Array<number>(numAttrib)
            .fill(0)
            .map(() => Math.random()),
    };

    // iterative optimization
    let param: number[];
    let delta = Infinity;
    let lastError = Infinity;
    while (delta > terminationThreshold) {
        // find param with minimal distances
        param = normalizedPoints.map(d =>
            closestCurvePoint(d, p0, curve, p3, weights)
        );
        // compute new p1, p2 with minimal distances
        curve = optimizeCurve(normalizedPoints, param, p0, curve, p3, weights);
        // compute error
        const newError = normalizedPoints
            .map((d, i) => projectionError(d, param[i], p0, curve, p3))
            .reduce((prev, curr) => prev + curr);
        delta = Math.abs(newError - lastError);
        lastError = newError;
    }

    // find final curve parameters with minimal distances
    param = normalizedPoints.map(d =>
        closestCurvePoint(d, p0, curve, p3, weights)
    );
    // create index array in param order
    return param
        .map((d, i) => [d, i])
        .sort((a, b) => a[0] - b[0])
        .map(d => d[1]);
}

/**
 * Returns the coordinates of the cubic bezier curve defined by the points p0-p3 at the parameter position s.
 * @param p0 the first control point
 * @param p1 the second control point
 * @param p2 the third control point
 * @param p3 the fourth control point
 * @param s the curve parameter
 * @returns
 */
function cubicBezier(
    p0: number[],
    p1: number[],
    p2: number[],
    p3: number[],
    s: number
): number[] {
    // check input
    const numAttrib = p0.length;
    if (
        p1.length !== numAttrib ||
        p2.length !== numAttrib ||
        p3.length !== numAttrib
    ) {
        console.warn("Inconsistent number of Dimensions");
        return Array(numAttrib).fill(0);
    }
    if (s < 0 || s > 1) {
        console.warn("Curve Parameter outside of range [0, 1]");
        return Array(numAttrib).fill(0);
    }

    // compute point coordinates
    const inverse = 1 - s;
    return p0.map(
        (d, i) =>
            inverse ** 3 * d +
            3 * inverse ** 2 * s * p1[i] +
            3 * inverse * s ** 2 * p2[i] +
            s ** 3 * p3[i]
    );
}

/**
 * Computes the squared distance between the given point and the given cubic bezier curve at parameter position s.
 * @param point the point
 * @param s the curve parameter
 * @param p0 the first control point of the curve
 * @param curve the second and third control points of the curve
 * @param p3 the fourth control point of the curve
 * @returns
 */
function projectionError(
    point: number[],
    s: number,
    p0: number[],
    curve: { p1: number[]; p2: number[] },
    p3: number[]
): number {
    const proj = cubicBezier(p0, curve.p1, curve.p2, p3, s);
    if (point.length !== proj.length) {
        console.warn("Inconsistent number of dimensions");
        return Infinity;
    }
    return point
        .map((d, i) => (d - proj[i]) ** 2)
        .reduce((prev, curr) => prev + curr);
}

/**
 * Estimates the curve parameter that most closely corresponds to the projection of the given point onto the given cubic bezier curve.
 * Samples the curve at 10000 positions and choses the parameter with the smallest distance.
 * @param point the given point
 * @param p0 the first control point of the curve
 * @param curve the second and third control points of the curve
 * @param p3 the fourth control point of the curve
 * @param weights the weights for each dimension
 * @returns
 */
function closestCurvePoint(
    point: number[],
    p0: number[],
    curve: { p1: number[]; p2: number[] },
    p3: number[],
    weights: number[]
): number {
    // check input
    const numAttrib = point.length;
    if (
        p0.length !== numAttrib ||
        curve.p1.length !== numAttrib ||
        curve.p2.length !== numAttrib ||
        p3.length !== numAttrib ||
        weights.length !== numAttrib
    ) {
        console.warn("Inconsistent number of dimensions");
        return 0.5;
    }

    // sample curve
    const t = Array.from(Array(10000).keys()).map(x => x * 0.0001);
    // compute weighted projection error for each sample
    const errors = t.map(d => {
        const p = cubicBezier(p0, curve.p1, curve.p2, p3, d);
        return p
            .map((d, i) => weights[i] * (point[i] - d) ** 2)
            .reduce((prev, curr) => prev + curr);
    });
    // return sample with smallest projection error
    return t
        .map((d, i) => [d, errors[i]])
        .reduce(
            (prev, curr) => (prev[1] < curr[1] ? prev : curr),
            [-1, Infinity]
        )[0];
}

/**
 * Computes a new second and third control point for the given cubic bezier curve to reduce the
 * projection error of the given datapoints, projected onto the curve at the given curve parameters.
 * @param data the data points
 * @param param the curve parameters
 * @param p0 the first control point of the curve
 * @param curve the second and third control points of the curve
 * @param p3 the fourth control point of the curve
 * @param weights the weights for each dimension
 * @returns
 */
function optimizeCurve(
    data: number[][],
    param: number[],
    p0: number[],
    curve: { p1: number[]; p2: number[] },
    p3: number[],
    weights: number[]
): { p1: number[]; p2: number[] } {
    // check input
    const numAttrib = weights.length;
    if (
        p0.length !== numAttrib ||
        curve.p1.length !== numAttrib ||
        curve.p2.length !== numAttrib ||
        p3.length !== numAttrib
    ) {
        console.warn("Inconsistent number of dimensions");
        return curve;
    }
    for (let i = 0; i < data.length; ++i) {
        if (data[i].length !== numAttrib) {
            console.warn("Inconsistent number of dimensions");
            return curve;
        }
    }
    if (param.length !== data.length) {
        console.warn(
            "Number of curve parameters does not match number of datapoints"
        );
        return curve;
    }

    // convert input data
    const s1 = tf.scalar(1);
    const s3 = tf.scalar(3);
    const sMaxLoss = tf.scalar(numAttrib * data.length);
    const xs = tf.tensor1d(Float32Array.of(...param));
    const ys = tf.tensor2d(Float32Array.of(...data.flat()), [
        data.length,
        numAttrib,
    ]);
    const cp0 = tf.tensor1d(Float32Array.of(...p0));
    const cp3 = tf.tensor1d(Float32Array.of(...p3));
    const w = tf.tensor1d(Float32Array.of(...weights));

    // define training parameters
    const cp1 = tf.tensor1d(Float32Array.of(...curve.p1)).variable();
    const cp2 = tf.tensor1d(Float32Array.of(...curve.p2)).variable();

    // define model function (cubic bezier curve)
    const f = (x: tf.Tensor1D) => {
        const inverse: tf.Tensor1D = tf.sub(s1, x);
        return tf.add(
            tf.add(
                tf.outerProduct(
                    tf.pow(inverse, s3) as tf.Tensor1D,
                    cp0
                ) as tf.Tensor2D, // (1-x)^3 * p0
                tf.outerProduct(
                    tf.mul(tf.mul(s3, tf.square(inverse)), x) as tf.Tensor1D,
                    cp1
                ) as tf.Tensor2D // (3 * (1-x)^2 * x * p1)
            ),
            tf.add(
                tf.outerProduct(
                    tf.mul(tf.mul(s3, inverse), tf.square(x)) as tf.Tensor1D,
                    cp2
                ) as tf.Tensor2D, // (3 * (1-x) * x^2 * p2)
                tf.outerProduct(
                    tf.pow(x, s3) as tf.Tensor1D,
                    cp3
                ) as tf.Tensor2D // x^3 * p3
            )
        ) as tf.Tensor2D;
    };

    // define loss function
    const loss = (pred: tf.Tensor2D, label: tf.Tensor2D) => {
        // determine whether any of the coefficients has gone out of the [0, 1] range
        // floor(x) * (ceil(x) - 1) should be 0 if and only if x is in range [0, 1]
        // sum up absolute value of all these dimensions of all vectors
        // this is = 0 if all original values are within [0, 1] and > 0 otherwise
        // the ceil of this factor is then = 0 for the valid [0, 1] values and >= 1 otherwise
        const rangeFactor: tf.Scalar = tf.ceil(
            tf.add<tf.Scalar>(
                tf.sum(tf.mul(tf.floor(cp1), tf.sub(tf.ceil(cp1), s1))),
                tf.sum(tf.mul(tf.floor(cp2), tf.sub(tf.ceil(cp2), s1)))
            )
        );
        // determine actual loss value
        const diff = tf.sum(
            tf.mul(tf.square(tf.sub(label, pred)), w)
        ) as tf.Scalar;
        // multiply range factor with the largest possible loss value, this is = 0 if all original values are within [0, 1]
        // then add the actual loss value
        return tf.add(tf.mul(rangeFactor, sMaxLoss), diff) as tf.Scalar;
    };

    // setup optimizer
    const learningRate = 0.001;
    const optimizer = tf.train.sgd(learningRate);

    // train optimizer
    for (let i = 0; i < 10; ++i) {
        optimizer.minimize(() => loss(f(xs), ys));
    }

    // get new point coordinates from tensors
    const newP1 = cp1.arraySync();
    const newP2 = cp2.arraySync();

    return {
        p1: newP1,
        p2: newP2,
    };
}
